import React from 'react';
import { AlertCircle, Mail, Phone, Building2, Shield, ExternalLink } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Badge } from '../ui/Badge';
import { VIPProfile as APIVIPProfile } from 'api-client';

// Extend the API VIPProfile type to ensure type safety
export type VIPProfile = {
  name: string;
  profilePicture?: string | null;
  title: string;
  email: string;
  phoneNumber: string;
  activeRisks: number;
  riskCount: number;
};

interface VIPProfileCardProps {
  profile: VIPProfile;
}

export const VIPProfileCard: React.FC<VIPProfileCardProps> = ({ profile }) => {
  const initials = profile.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  // Convert to numbers if needed
  const activeRisksCount = typeof profile.activeRisks === 'string' ? parseInt(profile.activeRisks, 10) : profile.activeRisks;
  const totalRisksCount = typeof profile.riskCount === 'string' ? parseInt(profile.riskCount, 10) : profile.riskCount;

  const getRiskIcon = (count: number, type: 'active' | 'total') => {
    if (count === 0) return null;

    const icons = {
      active: { icon: AlertCircle, color: 'text-red-500' },
      total: { icon: Shield, color: 'text-blue-500' }
    };

    const { icon: Icon, color } = icons[type];

    return (
      <Tooltip.Provider>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div className={`flex items-center space-x-1 ${color}`}>
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{count}</span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
              sideOffset={5}
            >
              {type === 'active' ? 'Active Risks' : 'Total Risks'}
              <Tooltip.Arrow className="fill-[#1A1F2E]" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  };

  return (
    <div className="bg-[#0F1629] rounded-xl overflow-hidden border border-gray-800/50 group">
      {/* Profile Image Section */}
      <div className="relative aspect-[2/1] overflow-hidden bg-gray-800">
        {profile.profilePicture ? (
          <>
            <img
              src={profile.profilePicture}
              alt={profile.name}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1629] to-transparent opacity-50" />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-700 text-2xl font-medium text-white">
              {initials}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold text-white">{profile.name}</h3>
            <div className="flex items-center space-x-2">
              {getRiskIcon(activeRisksCount, 'active')}
              {getRiskIcon(totalRisksCount, 'total')}
            </div>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Building2 className="h-4 w-4" />
            <span className="truncate">{profile.title}</span>
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Mail className="h-4 w-4" />
            <span className="truncate">{profile.email}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Phone className="h-4 w-4" />
            <span>{profile.phoneNumber}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
